// Quyết định `doctor --fix` được phép dời workspace con lên cwd hay không.
//
// HÀM THUẦN, 0 I/O — mọi dữ kiện do caller đo sẵn rồi truyền vào. Lý do: đây là chỗ
// DUY NHẤT phân định "tự động dời" với "để người quyết", mà vế sai của nó là dời nhầm
// code của user. Thuần thì test được TRỌN ma trận bằng literal, không phải dựng cây
// thư mục thật cho từng ca.
//
// Tiền đề ĐO ĐƯỢC (2026-09-16, `workspace-scaffold-and-finalize-orchestrator.ts:98-101`):
// `avatar init` LUÔN tạo 4 thứ ở workspace root — `src/` (rỗng), `notes/` (chỉ
// `orientation.md`), `.claude/`, `CLAUDE.md`. Nên luật KHÔNG thể là "thư mục con chỉ
// có .claude/" (không workspace thật nào như vậy) mà phải là "thư mục con chỉ chứa
// SCAFFOLD RỖNG". Phân biệt nằm ở NỘI DUNG `src/` và `notes/`, không ở sự có mặt của
// chúng:
//   - `src/` rỗng          = chưa `avatar add repo` ⇒ scaffold
//   - `src/` có thư mục con = repo code thật        ⇒ CHẶN, dời code là việc của người
//   - `notes/` chỉ `orientation.md` (pack seed)     ⇒ scaffold
//   - `notes/` có file khác = ghi chép của user     ⇒ CHẶN
// Entry top-level nào ngoài 4 cái trên (`docs/`, `plans/`, `.mattermost/`, `.git`…)
// đều là nội dung thật ⇒ CHẶN.

/** Entry top-level do `avatar init` dựng — ứng viên duy nhất được dời tự động. */
export const RELOCATABLE_SCAFFOLD_ENTRIES = [".claude", "CLAUDE.md", "notes", "src"] as const;

/**
 * Rác của hệ điều hành, KHÔNG tính là nội dung user. Danh sách cố ý ngắn và chỉ chứa
 * thứ do Finder/macOS tự sinh: mỗi tên thêm vào đây là một loại file thật bị bỏ qua
 * lúc quyết định dời.
 */
const IGNORED_NOISE_ENTRIES = [".DS_Store"] as const;

/** File pack tự seed vào `notes/` lúc init (#212) — không phải ghi chép của user. */
const NOTES_SCAFFOLD_FILES = ["orientation.md", ".DS_Store"] as const;

export type NestedWorkspaceRelocationBlocker =
  | "child-has-user-content"
  | "telemetry-queue-not-empty"
  | "cwd-path-collision";

export interface NestedWorkspaceRelocationFacts {
  /** Tên thư mục con (để dựng câu lệnh `./<tên>`). */
  childName: string;
  /** Entry top-level của thư mục con, gồm cả entry ẩn. */
  childEntries: string[];
  /** Entry bên trong `<con>/src` — mảng rỗng khi `src/` vắng mặt HOẶC rỗng thật. */
  srcEntries: string[];
  /** Entry bên trong `<con>/notes`. */
  notesEntries: string[];
  /** Số event telemetry còn nằm trong hàng đợi chưa gửi. */
  queuedTelemetryEvents: number;
  /** `<con>/.claude/settings.local.json` — quyền user tự thêm, dời nguyên .claude/ là giữ được. */
  hasSettingsLocal: boolean;
  /** Tool đang bật ở workspace con. */
  enabledTools: string[];
  /** Entry scaffold ĐÃ có sẵn ở cwd ⇒ dời lên là đè. */
  cwdCollisions: string[];
}

export interface NestedWorkspaceRelocationPlan {
  action: "relocate" | "manual";
  /** Entry sẽ dời, thứ tự ổn định theo RELOCATABLE_SCAFFOLD_ENTRIES. */
  entriesToMove: string[];
  blockers: NestedWorkspaceRelocationBlocker[];
  /** Nội dung thật đã phát hiện — đưa thẳng vào thông điệp, không nói chung chung. */
  userContent: string[];
  /** Dòng LIỆT KÊ in trước khi `--fix` động vào bất cứ thứ gì. */
  inventory: string[];
  /** Lệnh người dùng tự chạy khi không tự dời được. */
  manualInstructions: string[];
}

function isNoise(entry: string): boolean {
  return (IGNORED_NOISE_ENTRIES as readonly string[]).includes(entry);
}

/** Nội dung KHÔNG phải scaffold của `avatar init`. Rỗng ⇒ dời được. */
function findUserContent(facts: NestedWorkspaceRelocationFacts): string[] {
  const found: string[] = [];

  for (const entry of facts.childEntries) {
    if (isNoise(entry)) continue;
    if (!(RELOCATABLE_SCAFFOLD_ENTRIES as readonly string[]).includes(entry)) {
      found.push(`./${facts.childName}/${entry}`);
    }
  }

  // `src/` có bất cứ gì = đã `avatar add repo` ⇒ repo code thật, KHÔNG dời hộ.
  for (const entry of facts.srcEntries) {
    if (isNoise(entry)) continue;
    found.push(`./${facts.childName}/src/${entry}`);
  }

  for (const entry of facts.notesEntries) {
    if ((NOTES_SCAFFOLD_FILES as readonly string[]).includes(entry)) continue;
    found.push(`./${facts.childName}/notes/${entry}`);
  }

  return found.sort();
}

function buildInventory(facts: NestedWorkspaceRelocationFacts, userContent: string[]): string[] {
  const lines = [
    `workspace con: ./${facts.childName}`,
    `event telemetry chưa gửi trong hàng đợi: ${String(facts.queuedTelemetryEvents)}`,
    `settings.local.json riêng: ${facts.hasSettingsLocal ? "có (sẽ giữ nguyên)" : "không"}`,
    `tool đang bật: ${facts.enabledTools.length > 0 ? facts.enabledTools.join(", ") : "(không có)"}`,
  ];
  if (userContent.length > 0) lines.push(`nội dung KHÔNG phải scaffold: ${userContent.join(", ")}`);
  if (facts.cwdCollisions.length > 0)
    lines.push(`đã tồn tại ở thư mục hiện tại: ${facts.cwdCollisions.join(", ")}`);
  return lines;
}

export function planNestedWorkspaceRelocation(
  facts: NestedWorkspaceRelocationFacts,
): NestedWorkspaceRelocationPlan {
  const userContent = findUserContent(facts);
  const blockers: NestedWorkspaceRelocationBlocker[] = [];

  if (userContent.length > 0) blockers.push("child-has-user-content");
  // Gỡ workspace ra khỏi chỗ cũ khi hàng đợi còn event = mất đúng số event đó: flush
  // chạy theo `CLAUDE_PROJECT_DIR` của phiên, không ai đi tìm hàng đợi ở đường cũ.
  if (facts.queuedTelemetryEvents > 0) blockers.push("telemetry-queue-not-empty");
  // cwd không có `.claude/avatar.json` (điều kiện để check này chạy) VẪN có thể có
  // `.claude/` hoặc `CLAUDE.md` của riêng nó — dời đè lên là xoá file của user.
  if (facts.cwdCollisions.length > 0) blockers.push("cwd-path-collision");

  const entriesToMove = RELOCATABLE_SCAFFOLD_ENTRIES.filter((e) => facts.childEntries.includes(e));

  if (blockers.length === 0) {
    return {
      action: "relocate",
      entriesToMove: [...entriesToMove],
      blockers,
      userContent,
      inventory: buildInventory(facts, userContent),
      manualInstructions: [],
    };
  }

  const moveList = entriesToMove.map((e) => `./${facts.childName}/${e}`).join(" ");
  const manualInstructions: string[] = [];
  if (blockers.includes("telemetry-queue-not-empty")) {
    manualInstructions.push(
      `Gửi nốt hàng đợi trước: mở một phiên Claude Code ở ./${facts.childName} rồi đóng lại (SessionEnd tự flush), kiểm lại bằng 'avatar doctor'.`,
    );
  }
  if (blockers.includes("cwd-path-collision")) {
    manualInstructions.push(
      `Thư mục hiện tại đã có ${facts.cwdCollisions.join(", ")} — tự đặt tên khác hoặc dọn trước, Avatar KHÔNG ghi đè.`,
    );
  }
  if (blockers.includes("child-has-user-content")) {
    manualInstructions.push(
      `Thư mục con có nội dung thật (${userContent.join(", ")}) — dời code là việc của người. Nếu chắc chắn, tự chạy: mv ${moveList} . && rmdir ./${facts.childName}`,
    );
  }

  return {
    action: "manual",
    entriesToMove: [...entriesToMove],
    blockers,
    userContent,
    inventory: buildInventory(facts, userContent),
    manualInstructions,
  };
}
